import React from "react";
import { Link } from "react-router-dom";
import { inject, observer } from "mobx-react";
import { Checkbox, Glyphicon, Button, Radio } from "react-bootstrap";
import EntityDetailsModal from "../../../modal-views/EntityDetailsModal";
import { CustomTooltip } from "../../tooltip/CustomTooltip";
import CellContent from "../../vis/template/CellContent";
import { StoreContext } from "../../../contexts/StoreContext";

import "./Tree.scss";

@inject("http", "searchStore", "graphicalRepresentationStore", "popupStore")
@observer
class Tree extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showVariableDetailsModal: false,
      cubeTypePanelStates: {},
      tree: props.model ? this.initTree(props.model) : [],
      fullTree: props.unfilteredModel
        ? this.initTree(props.unfilteredModel)
        : [],
      variableToshow: null,
    };
  }

  componentDidUpdate(prevProps) {
    const { model, openItems, searchExpr, unfilteredModel } = this.props;
    const { visibleItems } = this.props.graphicalRepresentationStore;
    if (
      prevProps.openItems !== openItems ||
      prevProps.visibleItems !== visibleItems ||
      prevProps.searchExpr !== searchExpr
    ) {
      this.setState({ tree: model ? this.initTree(model) : [] });
      this.setState({
        fullTree: unfilteredModel ? this.initTree(unfilteredModel) : [],
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { model, openItems, searchExpr } = this.props;
    return (
      nextProps.model !== model ||
      nextProps.openItems !== openItems ||
      nextProps.searchExpr !== searchExpr ||
      nextState.hierarchyLimit !== this.state.hierarchyLimit ||
      nextState.cubeTypePanelStates !== this.state.cubeTypePanelStates ||
      nextState.variableToshow !== this.state.variableToshow
    );
  }
  handleToggleVariableDetailsModal = (e, detailsItem) => {
    e?.preventDefault();
    this.setState((oldState) => ({
      ...oldState,
      showVariableDetailsModal: !oldState.showVariableDetailsModal,
      variableToshow: { detailsItem, ...{ object: detailsItem } },
    }));
    return false;
  };

  initTree = (model) => {
    const { visibleItems, activeNode } =
      this.props.graphicalRepresentationStore;
    const arr = [];
    const processItem = (item, parentId = null) => {
      arr.push({
        id: item.id,
        isOpen: this.props.openItems.includes(item.id),
        isChecked: visibleItems.includes(item.id),
        isHighlighted: activeNode === item.id,
        hasChildren: !!item.children,
        parentId,
      });
      if (item.children) {
        item.children.forEach((child) => {
          processItem(child, item.id);
        });
      }
    };
    if (model.children && model.children.length) {
      model.children.forEach((child) => {
        processItem(child);
      });
    }
    return arr;
  };

  checkIfOpen = (id) => {
    const { openItems } = this.props;
    return openItems.includes(id);
  };

  checkIfSelected = (id) => {
    const { visibleItems } = this.props.graphicalRepresentationStore;
    return visibleItems.includes(id);
  };

  checkIfHighlighted = (id) => {
    const { visibleItems, activeNode } =
      this.props.graphicalRepresentationStore;
    return visibleItems.includes(id) && activeNode === id;
  };

  checkIfMatching = (child) => {
    const { searchExpr } = this.props;
    return (
      searchExpr &&
      child.name &&
      child.code &&
      (child.name.toLowerCase().includes(searchExpr.toLowerCase()) ||
        child.code.toLowerCase().includes(searchExpr.toLowerCase()))
    );
  };

  toggleOpen = (id) => {
    const { onOpenChange } = this.props;
    const { tree } = this.state;
    const newTree = [...tree];
    const target = newTree.find((item) => item.id === id);
    if (!target || !target.hasChildren) return;
    target.isOpen = !target.isOpen;
    onOpenChange(
      newTree.filter((item) => item.isOpen === true).map((item) => item.id)
    );
  };

  removeDuplicates = (array) => {
    return array.filter((value, index) => {
      const _value = JSON.stringify(value);
      return (
        index ===
        array.findIndex((obj) => {
          return JSON.stringify(obj) === _value;
        })
      );
    });
  };

  toggleSelected = (id) => {
    const { visibleItems, activeNode, setVisibleItems, setActiveNode } =
      this.props.graphicalRepresentationStore;
    const { isTemplateHierarchy } = this.props;
    const { fullTree } = this.state;
    const newTree = [...fullTree];

    const target = newTree.find((item) => item.id === id);
    if (!target) return;
    const checkState = target.isChecked;
    target.isChecked = !checkState;

    const toggleChildrenChecked = (parentId) => {
      const targetChildren = newTree.filter(
        (item) => item.parentId === parentId
      );
      if (targetChildren && targetChildren.length) {
        targetChildren.forEach((child) => {
          child.isChecked = !checkState;
          if (child.hasChildren === true) {
            toggleChildrenChecked(child.id);
          }
        });
      }
    };
    if (target.hasChildren === true) {
      toggleChildrenChecked(target.id);
    }
    if (target.parentId) {
      const targetParent = newTree.find((item) => item.id === target.parentId);
      if (targetParent) {
        const siblings = newTree.filter(
          (item) => item.parentId === target.parentId
        );
        const checked = siblings.filter((item) => item.isChecked === true);
        targetParent.isChecked = siblings.length === checked.length;
      }
    }
    if (isTemplateHierarchy) {
      setVisibleItems([target.id]);
      setActiveNode(visibleItems.includes(target.id) ? [target.id] : undefined);
      return;
    }

    const selectedNodes = newTree
      .filter((item) => item.isChecked === true)
      .map((item) => item.id);

    const unSelectedNodes = newTree
      .filter((item) => item.isChecked === false)
      .map((item) => item.id);
    const deletedNodes = visibleItems.filter(
      (item) => !unSelectedNodes.includes(item)
    );
    let visibleItemsToAdd = [...deletedNodes, ...selectedNodes];
    visibleItemsToAdd = this.removeDuplicates(visibleItemsToAdd);
    setVisibleItems(visibleItemsToAdd);
    setActiveNode(visibleItems.includes(activeNode) ? activeNode : undefined);
  };

  handleNodeSelection = (id) => {
    const { onActiveNodeChange } = this.props;
    onActiveNodeChange(id);
  };

  handleCubeTypePanelCollapse = (tid, limit, isOpen) => {
    this.setState((prevState) => ({
      ...prevState,
      cubeTypePanelStates: {
        ...prevState.cubeTypePanelStates,
        [tid]: { limit, isOpen },
      },
    }));
  };

  handleLimitVizualisation = (child) => {
    const { tid } = child;
    const isOpened =
      this.state.cubeTypePanelStates[tid] === undefined
        ? true
        : !this.state.cubeTypePanelStates[tid].isOpen;
    const length =
      this.state.cubeTypePanelStates[tid] === undefined
        ? child.sddMemberHierarchy.length
        : isOpened
        ? child.sddMemberHierarchy.length
        : 2;
    this.handleCubeTypePanelCollapse(tid, length, isOpened);
  };

  renderGroup = (children) => {
    if (!children) return null;
    const { groups } = this.props.graphicalRepresentationStore;
    const {
      rootItem,
      withSelection,
      withIcons,
      withIconLink,
      openModalTree,
      currentObject,
      isTemplateHierarchy,
    } = this.props;
    return (
      <ul>
        {children.map(
          (child, index) =>
            (!isTemplateHierarchy ||
              (isTemplateHierarchy && child.type !== "Cube")) && (
              <li key={child.id + index}>
                <div
                  className={`sdd-tree-item-label  ${
                    this.checkIfHighlighted(child.id) ? "highlighted" : ""
                  } ${this.checkIfMatching(child) ? "matching" : ""}`}
                >
                  {child.children &&
                    child.children.length > 0 &&
                    child.children[0].type !== "Cube" &&
                    isTemplateHierarchy && (
                      <Glyphicon
                        glyph={
                          this.checkIfOpen(child.id) === true
                            ? "chevron-down"
                            : "chevron-right"
                        }
                        onClick={() => this.toggleOpen(child.id)}
                      />
                    )}
                  {child.children &&
                    child.children.length > 0 &&
                    !isTemplateHierarchy && (
                      <Glyphicon
                        glyph={
                          this.checkIfOpen(child.id) === true
                            ? "triangle-bottom"
                            : "triangle-right"
                        }
                        onClick={() => this.toggleOpen(child.id)}
                      />
                    )}
                  {withSelection === true ? (
                    !isTemplateHierarchy ? (
                      <Checkbox
                        checked={this.checkIfSelected(child.id)}
                        onChange={() => {
                          this.toggleSelected(child.id);
                        }}
                        disabled={child.id === rootItem}
                        inline
                      ></Checkbox>
                    ) : (
                      child.children &&
                      child.children.length > 0 &&
                      child.children[0].type === "Cube" && (
                        <Radio
                          checked={this.checkIfSelected(child.id)}
                          onChange={() => {
                            this.toggleSelected(child.id);
                          }}
                          disabled={child.id === rootItem}
                          inline
                        >
                          {" "}
                        </Radio>
                      )
                    )
                  ) : null}
                  <div
                    onClick={() =>
                      child.children
                        ? this.toggleOpen(child.id)
                        : this.handleNodeSelection(child.id)
                    }
                    className="sdd-tree-item-name"
                  >
                    {withIcons === true
                      ? groups && (
                          <Glyphicon
                            glyph={
                              child.children &&
                              child.children[0] &&
                              child.children[0].type !== "Cube"
                                ? "folder-open"
                                : !isTemplateHierarchy
                                ? "th-large"
                                : "th"
                            }
                            style={{
                              color: groups[child.group]?.color?.background,
                            }}
                          />
                        )
                      : null}
                    {withIconLink === true ? (
                      <span>
                        {child.name} ({child.code})
                      </span>
                    ) : (
                      <span>
                        <Link
                          onClick={(e) => {
                            this.handleToggleVariableDetailsModal(e, child);
                          }}
                          to={`/view/${child.type}/${child.tid}`}
                          target="_blank"
                        >
                          <span>{child.name}</span>
                        </Link>
                        {` (${child.code}) `}
                      </span>
                    )}
                  </div>
                  <div className="member-hierarchy-tooltip-group">
                    <div>
                      {child.sddMemberHierarchy &&
                        child.sddMemberHierarchy
                          .filter((memberHierarchy) => {
                            return memberHierarchy.tid !== currentObject.tid;
                          })
                          .sort((a, b) => {
                            return a.name.length - b.name.length;
                          })
                          .slice(
                            0,
                            this.state.cubeTypePanelStates[child.tid] ===
                              undefined
                              ? 2
                              : this.state.cubeTypePanelStates[child.tid].limit
                          )
                          .map((hierarchyChild) => {
                            return (
                              <span
                                className="member-hierarchy-button"
                                key={hierarchyChild.tid}
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm btn-default"
                                  onClick={() =>
                                    openModalTree(hierarchyChild.tid)
                                  }
                                >
                                  <CellContent
                                    maxLength={40}
                                    content={hierarchyChild.name}
                                    id={0}
                                  />
                                </button>
                              </span>
                            );
                          })}
                      {child.sddMemberHierarchy &&
                      child.sddMemberHierarchy.filter((memberHierarchy) => {
                        return memberHierarchy.tid !== currentObject.tid;
                      }).length > 2 ? (
                        <Button
                          bsSize="xsmall"
                          onClick={() => this.handleLimitVizualisation(child)}
                          bsClass="btn btn-default btn-xs dropdown-toggle"
                          style={{ backgroundColor: "#003299", color: "white" }}
                        >
                          {`Read ${
                            this.state.cubeTypePanelStates[child.tid] ===
                            undefined
                              ? "more"
                              : this.state.cubeTypePanelStates[child.tid] &&
                                this.state.cubeTypePanelStates[child.tid].isOpen
                              ? "less"
                              : "more"
                          }`}
                        </Button>
                      ) : (
                        ""
                      )}
                    </div>
                    {child.sddMemberHierarchy &&
                    child.sddMemberHierarchy.filter((memberHierarchy) => {
                      return memberHierarchy.tid !== currentObject.tid;
                    }).length > 0 ? (
                      <div className="member-hierarchy-tooltip">
                        {child.sddMemberHierarchy && (
                          <CustomTooltip
                            placement="left"
                            type="Cube Link Variable"
                          />
                        )}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                  {withIconLink === true && child.type === "Cube" ? (
                    <span className="sdd-tree-item-link">
                      <Link
                        to={`/view/${child.type}/${child.tid}`}
                        target="_blank"
                      >
                        <Glyphicon glyph="new-window" />
                      </Link>
                    </span>
                  ) : null}
                </div>
                {child.children && this.checkIfOpen(child.id) === true
                  ? this.renderGroup(child.children)
                  : null}
              </li>
            )
        )}
      </ul>
    );
  };
  static contextType = StoreContext;
  render() {
    const { model } = this.props;
    const { variableToshow } = this.state;
    return (
      <div className="sdd-tree">
        {this.renderGroup(model.children)}
        {this.state.showVariableDetailsModal && (
          <StoreContext.Provider value={this.props.popupStore}>
            <EntityDetailsModal
              variable={null}
              cubeVariableName={"[No name]"}
              cubeVariableCode={null}
              details={variableToshow ? variableToshow : model.children[0]}
              onClose={(e) => {
                this.handleToggleVariableDetailsModal(e, variableToshow);
              }}
            />
          </StoreContext.Provider>
        )}
      </div>
    );
  }
}

export default Tree;
